import applicationImg from '@/assets/funnel35/application.webp';
import interviewImg from '@/assets/funnel35/interview.webp';
import morganImg from '@/assets/funnel35/morgan.webp';
import nvidiaImg from '@/assets/funnel35/nvidia.webp';
import { RA_BUTTON_CLASS } from './theme';

// Illustrations are the funnel 28 checkout set with only the outer background
// recoloured to the RemoteApply blue tint; the artwork inside is unchanged.
export const RA_FEATURES = [
  {
    title: 'Get matched with the best-fit jobs you’re most likely to land',
    description:
      'In seconds, we scan thousands of job boards and career pages to find open opportunities that match your salary goals, experience, and other preferences.',
    comparisons: ['Find 20x more relevant jobs', 'Cut search time by 90%'],
    image: nvidiaImg,
  },
  {
    title: 'We build your resume and tailor it perfectly for every application',
    description:
      'Our system builds you a professional-grade resume and personalizes it for any job — perfectly matching the role’s requirements and maximizing your chances of landing an interview.',
    comparisons: ['Get 10x more interviews', 'Land interviews 5x faster'],
    image: morganImg,
  },
  {
    title: 'Auto-apply to countless jobs instantly',
    description:
      'Apply to tons of companies faster and save hours by automatically filling and sending job applications — all tracked and managed in a fully automated dashboard.',
    comparisons: ['Send 50x more applications', 'Cut application time by 95%'],
    image: applicationImg,
  },
  {
    title: 'Ace any interview and negotiation',
    description:
      'Practice with our interview or negotiation simulator — get real-time personalized feedback, answers, and guidance. Or download an Answer Pack with tailored responses for your exact scenario.',
    comparisons: ['Get 5x more job offers', 'Get the highest offer possible'],
    image: interviewImg,
  },
];

const FeatureCard = ({ title, description, comparisons, image }) => (
  <article className="flex flex-col rounded-2xl border border-[#0000000F] bg-white p-4 lg:p-8">
    {image && <img src={image} alt="" className="mb-2 h-auto w-full rounded-2xl object-contain" />}
    <h3 className="mt-4 font-inter text-xl font-bold leading-[1.25] text-[#16202E] md:mt-6 md:text-2xl">{title}</h3>
    <p className="mt-4 font-inter text-[16px] leading-[1.6] text-[#535353] md:mt-6">{description}</p>
    <p className="mt-4 font-inter text-sm font-semibold text-[#16202E] md:mt-6">Compared to manual process:</p>
    <ul className="mt-3 flex list-none flex-col gap-3 p-0">
      {comparisons.map((text) => (
        <li
          key={text}
          className="flex items-center gap-2 rounded-[8px] bg-[#EEF3FF] px-4 py-2 font-inter text-[16px] leading-6 text-[#16202E]"
        >
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-[#2563EB]" />
          {text}
        </li>
      ))}
    </ul>
  </article>
);

/**
 * RemoteApply "Get job offers on autopilot" features grid (checkout / home).
 */
const FeaturesSection = ({
  title = 'Get job offers on autopilot',
  subtitle = 'We automate job hunting and help you at every step — so you can land your dream job with minimal time and effort',
  features = RA_FEATURES,
  firstButtonText = 'See plans',
  onFirstButtonClick,
  secondButtonText,
  onSecondButtonClick,
}) => (
  <section id="features" className="bg-white px-4 py-14 sm:px-6 lg:py-24">
    <div className="mx-auto w-full max-w-[1440px] lg:px-14">
      <div className="mb-8 text-center sm:mb-12">
        <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-[#16202E]/[0.07] px-4 py-2">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#2563EB]" />
          <span className="font-inter text-sm font-medium text-[#16202E]">Features</span>
        </div>
        <h2 className="font-inter text-[32px] font-bold leading-[1.15] text-[#16202E] md:text-[48px]">{title}</h2>
        {subtitle && (
          <p className="mx-auto mt-4 max-w-[560px] font-inter text-[16px] leading-[1.5] text-[#5A5A5A] md:text-[18px]">
            {subtitle}
          </p>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
        {features.map((feature) => (
          <FeatureCard key={feature.title} {...feature} />
        ))}
      </div>

      {(onFirstButtonClick || onSecondButtonClick) && (
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:mt-14 sm:flex-row">
          {onFirstButtonClick && (
            <button
              type="button"
              onClick={onFirstButtonClick}
              className={`${RA_BUTTON_CLASS} w-full px-7 py-4 text-[16px] sm:w-auto`}
            >
              {firstButtonText}
            </button>
          )}
          {onSecondButtonClick && secondButtonText && (
            <button
              type="button"
              onClick={onSecondButtonClick}
              className="flex w-full items-center justify-center rounded-full bg-[#EBEBEB] px-7 py-4 font-inter text-[16px] font-semibold text-[#16202E] transition-colors hover:bg-gray-200 sm:w-auto"
            >
              {secondButtonText}
            </button>
          )}
        </div>
      )}
    </div>
  </section>
);

export default FeaturesSection;
